'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import { usePathname } from 'next/navigation';
import { ComponentType, ReactNode } from 'react';
import { BaseTranslations } from '../../../../baseTranslations';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import { getFulfilmentPath } from '../../../../utils/tiAttributes';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import { resolveTiCtaLabel } from '../../../ApplyCta/resolveTiCtaLabel';
import { pushNavInteractionTi } from './navTracking';
import {
  DEFAULT_STICKY_OFFSET,
  StickyNavigationBarProps,
} from './StickyNavigationBar';
import { StickyNavigationBarDesktop } from './StickyNavigationBarDesktop';
import { StickyNavigationBarMobile } from './StickyNavigationBarMobile';

// Fallback TI sticky container (plain CSS sticky) used only if the fe wrapper
// does not supply a `navStickyContainer`. The fe wrapper normally supplies a
// `GlobalStickyPanel`-backed one so the nav actually pins in the nested layout.
const DefaultTiStickyContainer: ComponentType<{ children: ReactNode }> = ({
  children,
}) => <div className="sticky top-0 z-20 bg-fill/alternative-1">{children}</div>;

/**
 * TI provider page sticky navigation (CMS-1643). Per Figma viewport-scroll frames: a tier row on
 * top, then the section nav using the same md split as the default nav (desktop links >= md, mobile
 * dropdown < md), with the sibling page sections (FAQ / similar plans / reviews) appended as tabs and
 * the standardised TI Apply label. The whole thing pins via the fe-supplied `navStickyContainer`
 * (GlobalStickyPanel — portal + fixed) since CSS `sticky` breaks here; the column wrapper keeps it
 * aligned to the content column at rest, and the panel full-bleeds when pinned. `stickyOffset`,
 * `tierSlot`, `siblingNavigationItems`, `navStickyContainer` and `navPinned` come from the fe TI
 * wrapper through the module props.
 */
export const StickyNavigationBarTi: React.FC<StickyNavigationBarProps> = (
  props,
) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const stickyOffset = props.stickyOffset ?? DEFAULT_STICKY_OFFSET;
  const navigationItems = [
    ...props.navigationItems,
    ...(props.siblingNavigationItems ?? []),
  ];
  // Product-list name for the interaction events fired from the nav. Uses the
  // page path, matching the value the Product Hero sends, so all PDP events
  // carry a consistent, populated item_list_name instead of undefined.
  const itemListName = usePathname() ?? undefined;

  if (navigationItems.length === 0) {
    return null;
  }

  // Standardised apply label (Task 5) for the active tier's product.
  const applyLabel = resolveTiCtaLabel({
    brand: context.brand,
    staticText,
    fulfilmentPath: getFulfilmentPath(props.product),
    callToAction: props.product?.call_to_action,
  });

  // The nav CTA belongs to the provider page; the TI modal goes without it (CMS-1924).
  const showApplyCta = props.renderingMode === 'TI_PROVIDER';

  const Container = props.navStickyContainer ?? DefaultTiStickyContainer;
  return (
    <div className="max-w-[--breakpoints-content-narrow-3-lg] w-full lg:mx-auto px-spacing/padding/section-x lg:px-0">
      <Container>
        <div data-component="ti-product-nav">
          {props.tierSlot}
          <div className="hidden md:block">
            <StickyNavigationBarDesktop
              {...props}
              navigationItems={navigationItems}
              SPACER_COUNT={props.SPACER_COUNT}
              stickyOffset={stickyOffset}
              applyLabel={applyLabel}
              itemListName={itemListName}
              pushNavInteraction={pushNavInteractionTi}
              showApplyCta={showApplyCta}
              // Transparent in flow, backgrounded once pinned — so in flow a
              // backgrounded strip would be a stray patch.
              fadeStripBackground={Boolean(props.navPinned)}
            />
          </div>
          <div className="block md:hidden">
            <StickyNavigationBarMobile
              {...props}
              navigationItems={navigationItems}
              stickyOffset={stickyOffset}
              applyLabel={applyLabel}
              itemListName={itemListName}
              pushNavInteraction={pushNavInteractionTi}
              showApplyCta={showApplyCta}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};
